import React, { useMemo } from "react";
import { Text, VStack } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import {
  PaginatedTable,
  ServerFiltering,
  ServerPagination,
} from "@hydrologiq/shared/components/PaginatedTable";
import AlertStatusBadge, {
  AlertStatusType,
} from "@hydrologiq/shared/components/AlertStatusBadge";
import AlertSeverityBadge, {
  AlertSeverityType,
} from "@hydrologiq/shared/components/AlertSeverityBadge";
import { toUserTZ } from "@hydrologiq/common/helpers/date";
import { DateTime } from "timezonecomplete";
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy";
import {
  COPY_KEYS,
  DEFAULT_COPY_MAPPINGS,
} from "@hydrologiq/shared/copy/constants";
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import Link from "@hydrologiq/shared/components/Link"
// Format date as "MMM DD, YYYY HH:mm"
function formatAlertDate(dateTime: DateTime): string {
  return dateTime.format("MMM dd, yyyy HH:mm");
}

interface Alert {
  id: string;
  name: string;
  template_name?: string | null;
  type: string;
  severity: string;
  status: string;
  source: string;
  installation: string;
  installation_name?: string;
  message: string;
  fired_at: string;
  resolved_at?: string;
  created_at: string;
  asset_count: number;
  system_count?: number;
  systems?: { id?: string; name?: string | null; status?: string | null }[];
  hours_since_fired?: number;
}

interface AlertsTableProps {
  alerts: Alert[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  serverPagination?: ServerPagination;
  serverFiltering?: ServerFiltering;
  headerActions?: React.ReactNode;
  filterOptions?: any;
}

export function AlertsTable({
  alerts,
  loading,
  error,
  onRetry,
  serverPagination,
  serverFiltering,
  headerActions,
  filterOptions,
}: AlertsTableProps) {
  const navigate = useNavigate();
  const route = useRoute();

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ALERT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ALERT],
    [COPY_KEYS.HEADINGS.STATUS]:
      DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.STATUS],
    "headings.severity": "Severity",
    "headings.type": "Type",
    "headings.source": "Source",
    "headings.asset": "Asset",
    "headings.message": "Message",
    "headings.fired_at": "Fired At",
    "headings.assets": "Assets",
    "headings.systems": "Systems",
  });

  // Define table columns
  const columns = useMemo<ColumnDef<Alert, any>[]>(
    () => [
      {
        accessorKey: "name",
        header: copy[COPY_KEYS.HEADINGS.ALERT],
        cell: ({ row }) => {
          const alert = row.original;
          return (
            <Link
              variant="primary"
              onClick={() => navigate(`${route}${alert.id}`)}
              cursor="pointer"
            >
              {alert.template_name || alert.name}
            </Link>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "severity",
        header: copy["headings.severity"],
        cell: ({ getValue }) => {
          const severity = getValue() as AlertSeverityType;
          return <AlertSeverityBadge severity={severity} />;
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.severities ?? []).map(
            (o: { name: string; value: string }) => ({
              label: o.name,
              value: o.value,
            })
          ),
        },
      },
      {
        accessorKey: "status",
        header: copy[COPY_KEYS.HEADINGS.STATUS],
        cell: ({ getValue }) => {
          const status = getValue() as AlertStatusType;
          return <AlertStatusBadge status={status} />;
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.statuses ?? []).map(
            (o: { name: string; value: string }) => ({
              label: o.name,
              value: o.value,
            })
          ),
        },
      },
      {
        accessorKey: "type",
        header: copy["headings.type"],
        cell: ({ getValue }) => {
          const type = getValue() as string;
          const typeLabels = {
            status: "Status",
            pad: "PAD",
            maintenance: "Maintenance",
            anomaly: "Anomaly",
            performance: "Performance",
          };
          return (
            <Text>{typeLabels[type as keyof typeof typeLabels] || type}</Text>
          );
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.types ?? []).map(
            (o: { name: string; value: string }) => ({
              label: o.name,
              value: o.value,
            })
          ),
        },
      },
      {
        accessorKey: "source",
        header: copy["headings.source"],
        cell: ({ getValue }) => {
          const source = getValue() as string;
          const sourceLabels = {
            grafana: "Grafana",
            internal: "Internal",
            vendor: "Vendor",
          };
          return (
            <Text>
              {sourceLabels[source as keyof typeof sourceLabels] || source}
            </Text>
          );
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.sources ?? []).map(
            (o: { name: string; value: string }) => ({
              label: o.name,
              value: o.value,
            })
          ),
        },
      },
      {
        accessorKey: "asset_count",
        id: "asset",
        header: copy["headings.asset"],
        cell: ({ row }) => {
          const alert = row.original;
          const assetCount = alert.asset_count || 0;
          if (assetCount === 0) {
            return <Text color="gray.500">No assets</Text>;
          } else if (assetCount === 1) {
            return <Text>1 asset</Text>;
          } else {
            return <Text>{assetCount} assets</Text>;
          }
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.assets ?? []).map(
            (a: { name: string; value: string }) => ({
              label: a.name,
              value: a.value,
            })
          ),
        },
      },
      {
        accessorKey: "system_count",
        id: "system",
        header: copy["headings.systems"],
        cell: ({ row }) => {
          const alert = row.original;
          const n = alert.system_count ?? 0;
          if (n === 0) {
            return <Text color="gray.500">—</Text>;
          }
          const names = (alert.systems ?? [])
            .map((s) => s.name)
            .filter(Boolean)
            .join(", ");
          return (
            <Text truncate maxW="220px" title={names || undefined}>
              {names || `${n} system(s)`}
            </Text>
          );
        },
        meta: {
          filterVariant: "select",
          filterOptions: (filterOptions?.systems ?? []).map(
            (s: { name: string; value: string }) => ({
              label: s.name,
              value: s.value,
            })
          ),
        },
      },
      {
        accessorKey: "message",
        header: copy["headings.message"],
        cell: ({ getValue }) => {
          const message = getValue() as string;
          return (
            <Text truncate maxW="300px" title={message}>
              {message}
            </Text>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "fired_at",
        header: copy["headings.fired_at"],
        cell: ({ getValue }) => {
          const firedAt = getValue() as string;
          return <Text>{formatAlertDate(toUserTZ(firedAt))}</Text>;
        },
        enableColumnFilter: false,
      },
    ],
    [copy, navigate, filterOptions]
  );

  return (
    <VStack gap={4} align="stretch">
      <PaginatedTable
        data={alerts}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={onRetry}
        serverPagination={serverPagination}
        serverFiltering={serverFiltering}
        headerActions={headerActions}
        emptyState={{
          heading: "No alerts found",
          text: "No alerts match your current filters",
        }}
        entityName="alerts"
      />
    </VStack>
  );
}

export default AlertsTable;
